/**
 * =============================================================================
 * SPEQ55 RFP Pro - Forgot Password Page
 * =============================================================================
 *
 * This component provides the "forgot password" interface where users can
 * request a password reset email. It handles:
 *
 * - Email input with validation
 * - Form submission to the reset endpoint
 * - Success/error message display
 * - Link back to login page
 *
 * SECURITY CONSIDERATIONS:
 * ------------------------
 * - Always shows success message regardless of whether email exists
 *   (prevents email enumeration attacks)
 * - Rate limited on the backend to prevent abuse
 * - No indication of whether account exists
 *
 * USER EXPERIENCE:
 * ----------------
 * - Clear instructions on what to expect
 * - Disabled form after successful submission
 * - Option to try again with different email
 * - Direct link back to login
 *
 * @module pages/auth/ForgotPassword
 * @author SPEQ55
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import speq55Logo from '../../assets/speq55-logo.png';

// =============================================================================
// FORGOT PASSWORD PAGE COMPONENT
// =============================================================================

/**
 * Forgot Password page component.
 *
 * Renders a form for users to request a password reset email.
 * On submission, always shows success to prevent email enumeration.
 *
 * @returns {JSX.Element} Forgot password page
 */
function ForgotPassword() {
  // ===========================================================================
  // STATE
  // ===========================================================================

  /**
   * Email input value.
   */
  const [email, setEmail] = useState('');

  /**
   * Validation error for email field.
   */
  const [emailError, setEmailError] = useState('');

  /**
   * General error message (e.g., network error).
   */
  const [generalError, setGeneralError] = useState('');

  /**
   * Loading state during form submission.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Whether the form has been successfully submitted.
   * Used to show success message and disable form.
   */
  const [isSubmitted, setIsSubmitted] = useState(false);

  // ===========================================================================
  // FORM HANDLERS
  // ===========================================================================

  /**
   * Handles email input changes.
   *
   * @param {Object} event - Input change event
   */
  const handleEmailChange = (event) => {
    setEmail(event.target.value);

    // Clear errors when user starts typing
    if (emailError) {
      setEmailError('');
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  /**
   * Validates the email before submission.
   *
   * @returns {boolean} True if email is valid
   */
  const validateEmail = () => {
    // Check if email is provided
    if (!email.trim()) {
      setEmailError('Email address is required');
      return false;
    }

    // Basic email format validation
    // More thorough validation happens on the server
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  /**
   * Handles form submission.
   * Sends password reset request and shows success message.
   *
   * @param {Object} event - Form submit event
   */
  const handleSubmit = async (event) => {
    // Prevent default form submission
    event.preventDefault();

    // Clear previous errors
    setGeneralError('');

    // Validate email
    if (!validateEmail()) {
      return;
    }

    // Set loading state
    setIsSubmitting(true);

    try {
      // Send password reset request
      await api.post('/auth/forgot-password', { email });

      // Always mark as submitted successfully
      // Server returns 200 even if email doesn't exist (security measure)
      setIsSubmitted(true);
    } catch (error) {
      // Handle network/server errors
      // Even on error, we might want to show success to prevent enumeration
      // But if it's a clear network error, show that
      if (error.code === 'ERR_NETWORK' || !error.response) {
        setGeneralError('Unable to connect to the server. Please check your internet connection.');
      } else {
        // For other errors, still show success (security through obscurity)
        setIsSubmitted(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Resets the form to try again with a different email.
   */
  const handleTryAgain = () => {
    setEmail('');
    setIsSubmitted(false);
    setEmailError('');
    setGeneralError('');
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/*
        Main container - centered card
        Consistent with login page styling
      */}
      <div className="max-w-md w-full space-y-8">
        {/* Header Section */}
        <div className="text-center">
          {/* Logo/Brand */}
          <div className="flex justify-center mb-4">
            <img src={speq55Logo} alt="SPEQ55" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-blue-600">RFP Pro</h1>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Reset your password</h2>
          <p className="mt-2 text-sm text-gray-600">
            {isSubmitted
              ? 'Check your email for reset instructions'
              : "Enter your email and we'll send you a reset link"}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10">
          {/*
            Success Message
            Shown after successful form submission
          */}
          {isSubmitted ? (
            <div className="space-y-6">
              {/* Success icon and message */}
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                  <svg
                    className="h-8 w-8 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">Check your email</h3>
                <p className="mt-2 text-sm text-gray-600">
                  If an account with that email exists, we've sent password reset instructions to{' '}
                  <strong>{email}</strong>.
                </p>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">What's next?</h4>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the reset link in the email</li>
                  <li>Create a new password</li>
                  <li>The link expires in 24 hours</li>
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleTryAgain}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try a different email
                </button>
                <Link
                  to="/login"
                  className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-center"
                >
                  Back to login
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/*
                General Error Alert
                Shown for network errors or unexpected issues
              */}
              {generalError && (
                <div
                  className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded"
                  role="alert"
                  aria-live="polite"
                >
                  <div className="flex">
                    <svg
                      className="h-5 w-5 text-red-500 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm text-red-700">{generalError}</p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={handleEmailChange}
                      disabled={isSubmitting}
                      className={`
                        appearance-none block w-full px-3 py-2 border rounded-md shadow-sm
                        placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500
                        sm:text-sm
                        ${emailError ? 'border-red-500 text-red-900' : 'border-gray-300'}
                        ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}
                      `}
                      placeholder="Enter your email address"
                      aria-invalid={emailError ? 'true' : 'false'}
                      aria-describedby={emailError ? 'email-error' : undefined}
                    />
                  </div>
                  {/* Field-specific error */}
                  {emailError && (
                    <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`
                      w-full flex justify-center py-2.5 px-4 border border-transparent
                      rounded-md shadow-sm text-sm font-medium text-white
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                      ${
                        isSubmitting
                          ? 'bg-blue-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }
                    `}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Sending reset link...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </div>

                {/* Back to login link */}
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    ← Back to login
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500">
          Remember your password?{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default ForgotPassword;
